/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import useMounted from '../../../shared/useMount/useMount';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import ManualInputTabDetailsComponent from './ManualInputTabDetailsComponent';
import { useFileUploadContext } from '../../../../context/FileUploadContext/FileUploadContext';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import {
  fuelModalDropDown,
  fuelModalProps,
  IfuelModalDropDownWithLoaderContent
} from './ManualInputConfiguration';
import { fuelGasTypeLabelCheck } from '../AddFuelUtils';

const ManualInputTabDetailsWithLoaderContent = (
  props: IfuelModalDropDownWithLoaderContent
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, details, tableData, countryCodeFromParent } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        <ManualInputTabDetailsComponent
          data={data}
          details={details}
          tableData={tableData}
          activeIndexDataUpload={props?.activeIndexDataUpload}
          toggleFeatureAllUpload={props?.toggleFeatureAllUpload}
          toggleFeatureScopeOne={props?.toggleFeatureScopeOne}
          toggleFeatureScopeTwo={props?.toggleFeatureScopeTwo}
          countryCodeFromParent={countryCodeFromParent}
          toggleFeatureGAS={props?.toggleFeatureGAS}
        />
      </div>
    </div>
  );
};

const ManualInputTabDetailsWithLoader = withLoadingStatus(
  withErrorStatus(ManualInputTabDetailsWithLoaderContent)
);

const ManualInputTabDetailsComponentWidget = (props: fuelModalProps) => {
  const [state] = useFileUploadContext();
  const { requestContext } = state;
  const { countryCode } = props;
  const [t] = useTranslation();
  const e = descriptors.energyManagement;
  const getCombustionName = fuelGasTypeLabelCheck(
    props.activeIndexDataUpload,
    'Stationary Combustion',
    'Refrigerants',
    props.toggleFeatureAllUpload,
    props.toggleFeatureScopeOne,
    props.toggleFeatureScopeTwo,
    'Scope2',
    props.toggleFeatureGAS,
    'GAS'
  );

  const connectorConfig: any = {
    name: 'manual-upload-add_fules_dropdown',
    options: {
      combustionName: getCombustionName === 'GAS' ? 'Scope2' : getCombustionName
    }
  };
  const isMounted = useMounted();
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext
  });

  useEffect(() => {
    if (isMounted) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
    }
  }, [requestContext]);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const AddFuelData = (all as unknown) as fuelModalDropDown[];
  const isDataAvailable = AddFuelData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as fuelModalDropDown[]);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(AddFuelData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      {state?.modalType === 'editFuelData' ||
      state?.modalType === 'missingData' ? (
        <ManualInputTabDetailsComponent
          details={props}
          data={data}
          activeIndexDataUpload={props?.activeIndexDataUpload}
          toggleFeatureAllUpload={props?.toggleFeatureAllUpload}
          toggleFeatureScopeOne={props?.toggleFeatureScopeOne}
          toggleFeatureScopeTwo={props?.toggleFeatureScopeTwo}
          countryCodeFromParent={countryCode}
          toggleFeatureGAS={props?.toggleFeatureGAS}
        />
      ) : (
        <ManualInputTabDetailsWithLoader
          loading={isLoading}
          error={errorInfo}
          statusCode={statusCode}
          onRefresh={onRefresh}
          data={data}
          details={props}
          loadingText={t(e.LOADING)}
          title={t(e.CONNECTIONERRORTITLE)}
          noDataMessage={t(e.NODATAAVAILABLE)}
          connectionErrorMessage={t(e.CONNECTIONERROR)}
          tableData={props.tableData}
          activeIndexDataUpload={props.activeIndexDataUpload}
          toggleFeatureAllUpload={props.toggleFeatureAllUpload}
          toggleFeatureScopeOne={props?.toggleFeatureScopeOne}
          toggleFeatureScopeTwo={props?.toggleFeatureScopeTwo}
          countryCodeFromParent={countryCode}
          toggleFeatureGAS={props?.toggleFeatureGAS}
        />
      )}
    </>
  );
};
export default ManualInputTabDetailsComponentWidget;
